import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { dispatch } from "../../../../../state/store";
import { setSelectedApp } from "../../../../../state/ducks";

import { appsService, notificationService, t } from "../../../../../services";
import { appGeneralConfigurationPage, appManageTypes, strCode } from "../../../../../consts";
import { responseUtil } from "../../../../../utils";

import { Heading5, Modal, PageSelector } from "../../../../../components";

import FeaturesModalTable from "./tables/features/FeaturesModalTable";
import FeaturesModalControlButtons from "./FeaturesModalControlButtons";
import { PageSelectorWrapper } from "./styled";
import CustomFeaturesModalTable from "./tables/custom/CustomFeaturesModalTable";

const configurationPages = [
  { id: appGeneralConfigurationPage.FEATURES, title: strCode.FEATURES_TITLE },
  { id: appGeneralConfigurationPage.CUSTOM, title: strCode.CUSTOM_TITLE }
];

const FeaturesModal = ({ isActive, setActive }) => {
  const [configurationPage, setConfigurationPage] = useState(appGeneralConfigurationPage.FEATURES);

  const [featuresModalData, setFeaturesModalData] = useState([]);
  const [editingFeature, setEditingFeature] = useState(null);

  const [customFeaturesModalData, setCustomFeaturesModalData] = useState([]);
  const [editingCustomFeature, setEditingCustomFeature] = useState(null);

  const selectedApp = useSelector((store) => store.apps.selectedApp);
  const manageType = useSelector((store) => store.apps.manageType);
  const lastActiveIndex = useSelector((store) => store.apps.lastActiveIndex);

  const isSaveDisabled = useMemo(() => {
    return (
      featuresModalData.some((feature) => !feature.code) ||
      customFeaturesModalData.some((feature) => !feature.name || !feature.value)
    );
  }, [featuresModalData]);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isActive) {
      setFeaturesModalData(selectedApp?.defaultSettings ?? []);
      setCustomFeaturesModalData(selectedApp?.customFeatures ?? []);
    }
  }, [isActive]);

  const onAddFeature = (feature) => {
    setEditingFeature(feature);
    setFeaturesModalData([...featuresModalData, feature]);
  };

  const onAddCustomFeature = (feature) => {
    setEditingCustomFeature(feature);
    setCustomFeaturesModalData([...customFeaturesModalData, feature]);
  };

  const onCanselFeatureEditing = () => {
    setFeaturesModalData(featuresModalData.filter((feature) => feature.settingName));

    setEditingFeature(null);
  };

  const onCancelCustomFeatureEditing = () => {
    setCustomFeaturesModalData((prev) =>
      prev
        .filter((feature) => feature.name && feature.value)
        .map((feature) => ({ ...feature, isEdit: false }))
    );
    setEditingCustomFeature(null);
  };

  const onSaveFeatureEditing = (newFeatureName, editingFeatureName) => {
    if (featuresModalData.some((feature) => feature.settingName === newFeatureName)) {
      throw new Error(strCode.APP_UPDATE_ERROR_TITLE);
    }

    const featureCode = newFeatureName.split(" ").join("_").toUpperCase();

    setFeaturesModalData(
      featuresModalData.map((feature) => {
        if (feature.settingName === editingFeatureName) {
          return {
            code: feature.settingName ? feature.code : featureCode.toUpperCase(),
            settingName: newFeatureName,
            value: feature.value
          };
        }

        return feature;
      })
    );

    setEditingFeature(null);
  };

  const onSaveCustomFeatureEditing = () => {
    const existingFeature = customFeaturesModalData.find(
      (feature) => feature.name === editingCustomFeature?.name
    );

    if (existingFeature && !existingFeature.isEdit) {
      throw new Error(strCode.APP_UPDATE_ERROR_TITLE);
    }

    setCustomFeaturesModalData((prev) =>
      prev.map((feature) => {
        if (feature.isEdit) {
          return {
            ...editingCustomFeature,
            isEdit: false
          };
        }

        return feature;
      })
    );

    setEditingCustomFeature(null);
  };

  const onDeleteFeature = (featureName) => {
    setFeaturesModalData(
      featuresModalData.filter((feature) => feature.settingName !== featureName)
    );
  };

  const onDeleteCustomFeature = (name) => {
    setCustomFeaturesModalData((prev) => prev.filter((feature) => feature.name !== name));
  };

  const onSetEditingCustomFeature = (feature) => {
    setCustomFeaturesModalData((prev) =>
      prev.map((f) => {
        if (f.name === feature.name) {
          return {
            ...f,
            isEdit: true
          };
        }

        return f;
      })
    );

    setEditingCustomFeature(feature);
  };

  const onToggleFeature = (featureName) => {
    setFeaturesModalData(
      featuresModalData.map((feature) => {
        if (feature.settingName === featureName) {
          return {
            ...feature,
            value: !feature.value
          };
        }

        return feature;
      })
    );
  };

  const onModalSave = async () => {
    if (isLoading) {
      return;
    }

    const preparedCustomFeature = customFeaturesModalData.map((feature) => {
      return { name: feature.name, value: feature.value };
    });

    let res = null;

    if (manageType === appManageTypes.EDIT || lastActiveIndex > 0) {
      try {
        setIsLoading(true);

        res = await appsService.updateApp(
          {
            appType: selectedApp.appType,
            name: selectedApp.name,
            defaultSettings: featuresModalData,
            customFeatures: preparedCustomFeature
          },
          strCode.CONFIGURATION_SAVE_ERROR_TITLE
        );
      } finally {
        setIsLoading(false);
      }
    }

    if ((res && responseUtil.isResponseSuccessful(res)) || manageType !== appManageTypes.EDIT) {
      notificationService.showSuccess({
        title: t(strCode.CONFIGURATION_SAVED_TITLE),
        message: t(strCode.CONFIGURATION_SAVED_DESCRIPTION)
      });

      dispatch(
        setSelectedApp({
          ...selectedApp,
          defaultSettings: featuresModalData,
          customFeatures: preparedCustomFeature
        })
      );

      setActive();
    }
  };

  useEffect(() => {
    if (!isActive) {
      setConfigurationPage(appGeneralConfigurationPage.FEATURES);
      setEditingFeature(null);
      setEditingCustomFeature(null);
    }
  }, [isActive]);

  return (
    <Modal
      id="features-settings-modal"
      active={isActive}
      setActive={setActive}
      closable={false}
      modalHeader={<Heading5>{t(strCode.CONFIGURE_FEATURES_TITLE)}</Heading5>}
      modalFooter={
        <FeaturesModalControlButtons
          onCancel={setActive}
          onSave={onModalSave}
          isSaveDisabled={isSaveDisabled}
          loading={isLoading}
        />
      }>
      <PageSelectorWrapper>
        <PageSelector
          pages={configurationPages}
          activePage={configurationPage}
          setActivePage={setConfigurationPage}
        />
      </PageSelectorWrapper>

      {configurationPage === appGeneralConfigurationPage.FEATURES && (
        <FeaturesModalTable
          features={featuresModalData}
          editingFeature={editingFeature}
          setEditingFeature={setEditingFeature}
          onAddFeature={onAddFeature}
          onCanselEditing={onCanselFeatureEditing}
          onSaveEditing={onSaveFeatureEditing}
          onDeleteFeature={onDeleteFeature}
          onToggleValue={onToggleFeature}
        />
      )}

      {configurationPage === appGeneralConfigurationPage.CUSTOM && (
        <CustomFeaturesModalTable
          customFeatures={customFeaturesModalData}
          editingCustomFeature={editingCustomFeature}
          setEditingCustomFeature={setEditingCustomFeature}
          onAddCustomFeature={onAddCustomFeature}
          onSaveCustomFeatureEditing={onSaveCustomFeatureEditing}
          onCancelCustomFeatureEditing={onCancelCustomFeatureEditing}
          onDeleteCustomFeature={onDeleteCustomFeature}
          onSetEditingCustomFeature={onSetEditingCustomFeature}
        />
      )}
    </Modal>
  );
};

FeaturesModal.propTypes = {
  isActive: PropTypes.bool.isRequired,
  setActive: PropTypes.func.isRequired
};

export default FeaturesModal;
